<template>
  <div class="roles">
    <v-row>
      <v-col cols="12" sm="8">
        <div class="areaTitle">
          <h2>Roles</h2>

          <v-btn color="primary" depressed @click.stop="onEditRole(null)" class="mr-2">
            <v-icon class="mr-2">mdi-plus</v-icon>
            Add new role
          </v-btn>
        </div>
        <v-card class="pa-2 rolesList" outlined tile>
          <v-data-table
            :headers="headers"
            :items="roles"
            :loading="loading"
            :items-per-page="-1"
            :single-select="true"
            item-key="RoleId"
            @click:row="rowClick"
            dense
          >
            <template v-slot:item.Actions="{ item }">
              <div class="actionBtnContainer">
                <v-btn
                  color="primary"
                  depressed
                  @click.stop="onEditRole(item)"
                  small
                  class="actionBtn mr-2"
                >
                  Edit
                </v-btn>
                <v-btn
                  color="primary"
                  depressed
                  @click.stop="onEditPermissions(item)"
                  small
                  class="actionBtn mr-2"
                >
                  Permissions
                </v-btn>
                <v-btn
                  color="error"
                  depressed
                  @click.stop="onDeleteRole(item)"
                  small
                  class="actionBtn mr-2"
                  ><v-icon small> mdi-delete </v-icon>
                </v-btn>
              </div>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
      <v-divider vertical></v-divider>
      <v-col cols="12" sm="4" class="userList">
        <div class="areaTitle usersTitle">
          <h2>Role users</h2>

          <v-btn
            v-if="selectedRole"
            color="primary"
            depressed
            @click.stop="onManageUsers()"
            :disabled="!isAvailableAddNewUser"
            class="mr-2"
          >
            <v-icon class="mr-2 manageIcon">mdi-plus</v-icon>
            Add user
          </v-btn>
        </div>
        <div v-if="!selectedRole">
          <v-alert type="info" outlined dense>
            Please, select a role first to view the role users
          </v-alert>
        </div>
        <div v-else>
          <div v-if="loadingUsers">
            <span>
              <v-progress-circular
                indeterminate
                :size="20"
                color="blue lighten-5"
                class="mr-2"
              ></v-progress-circular
              >Loading..
            </span>
          </div>
          <div v-else-if="roleUsers && roleUsers.length > 0">
            <v-text-field
              v-model="userSearch"
              append-icon="mdi-magnify"
              label="Search"
              class="mb-2 search"
              single-line
              hide-details
            ></v-text-field>
            <v-data-table
              :headers="usersHeaders"
              :items="roleUsers"
              item-key="Id"
              :items-per-page="-1"
              :loading="loadingUsers"
              :search="userSearch"
              hide-default-header
              loading-text="Loading users list..."
              :calculate-widths="true"
              class="searchResults"
              dense
              show-expand
            >
              <template v-slot:item.Actions="{ item }">
                <div class="actionBtnContainer">
                  <v-btn
                    color="error"
                    depressed
                    @click.stop="onDeleteUser(item)"
                    small
                    class="actionBtn mr-2"
                    ><v-icon small> mdi-delete </v-icon>
                  </v-btn>
                </div>
              </template>
              <template v-slot:expanded-item="{ headers, item }">
                <td :colspan="headers.length">
                  <UserPlans
                    :userName="item.ShortName"
                    :roleId="selectedRole.RoleId"
                    :plansList="planList"
                  ></UserPlans>
                </td>
              </template>
            </v-data-table>
          </div>
          <div v-else>
            <v-alert type="info" outlined dense>
              No users. Please set up the role to the users.
            </v-alert>
          </div>
        </div>
      </v-col>
    </v-row>
    <v-dialog v-model="editRoleDialog" persistent max-width="600px">
      <v-card v-if="editRole">
        <v-card-title>
          <span class="text-h5">Role</span>
        </v-card-title>
        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="12">
                <v-text-field label="Name" v-model="editRole.Name" required></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-textarea
                  solo
                  name="note"
                  label="Description"
                  v-model="editRole.Description"
                ></v-textarea>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" outlined text @click="onCancelEditRole"> Cancel </v-btn>
          <v-btn color="primary" @click="onSaveEditRole" :disabled="!editRole.Name"> Save </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="editRolePermissionsDialog" persistent max-width="1000px">
      <v-card v-if="editRole">
        <v-card-title>
          <span class="text-h5">Permissions</span>
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12">
              <div v-if="loadingPermissions">
                <span>
                  <v-progress-circular
                    indeterminate
                    :size="20"
                    color="blue lighten-5"
                    class="mr-2"
                  ></v-progress-circular
                  >Loading..
                </span>
              </div>
              <div
                v-else-if="rolePermissions && rolePermissions.length > 0"
                class="permissionsContainer"
              >
                <v-row>
                  <v-col cols="6" class="permission">
                    <h3>Full permissions:</h3>
                    <v-select
                      v-model="fullPermissions"
                      :items="availableFullPermissions"
                      item-text="ShortName"
                      item-value="Id"
                      attach
                      chips
                      label=""
                      multiple
                    ></v-select>
                  </v-col>
                  <v-col cols="6" class="permission">
                    <h3>Limited permissions:</h3>
                    <v-select
                      v-model="limitedPermissions"
                      :items="availableLimitedPermissions"
                      item-text="ShortName"
                      item-value="Id"
                      attach
                      chips
                      label=""
                      multiple
                    ></v-select>
                  </v-col>
                  <!-- <v-col
                      cols="4"
                      v-for="permission in rolePermissions"
                      :key="permission.Id"
                      class="permission"
                    >
                      <v-select
                        v-model="permission.Reference"
                        :items="permissionsList"
                        :label="permission.ShortName"
                        outlined
                        dense
                      ></v-select>
                    </v-col> -->
                </v-row>
              </div>
            </v-col>
          </v-row>
          <div>*Not selected item will be in the "Denied" status.</div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" outlined text @click="onCancelPermissions"> Cancel </v-btn>
          <v-btn color="primary" @click="onSavePermissions"> Save </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="addUserDialog" persistent max-width="600px">
      <v-card>
        <v-card-title>
          <span class="text-h5">Add user to role</span>
        </v-card-title>
        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="12">
                <v-select
                  :items="availableUsers"
                  item-value="UserId"
                  item-text="UserName"
                  v-model="newUser"
                  label="Select user"
                  dense
                  outlined
                >
                </v-select>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" outlined text @click="onCancelAddUsers"> Cancel </v-btn>
          <v-btn color="primary" @click="onAddNewUser" :disabled="!newUser"> Save </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <Dialog ref="confirmation"></Dialog>
  </div>
</template>
<script>
import Dialog from '@/components/layout/Dialog.vue';
import UserPlans from './Components/UserPlans.vue';

export default {
  name: 'Roles',
  components: {
    Dialog,
    UserPlans,
  },
  data() {
    return {
      roles: [],
      rolePermissions: null,
      fullPermissions: [],
      limitedPermissions: [],
      roleUsers: null,
      users: [],
      planList: [],
      headers: [
        { text: 'Name', value: 'Name' },
        { text: 'Description', value: 'Description' },
        { text: '', value: 'Actions' },
      ],
      usersHeaders: [
        { text: 'Name', value: 'ShortName' },
        { text: '', value: 'Actions', filterable: false },
      ],
      loading: false,
      loadingPermissions: false,
      loadingUsers: false,
      selectedRole: null,
      editRole: null,
      editRoleDialog: false,
      editRolePermissionsDialog: false,
      addUserDialog: false,
      permissionsList: ['', 'Full', 'Limited', 'Denied'],
      userSearch: null,
      newUser: null,
    };
  },
  mounted: function () {
    this.getRoles();
    this.getPlanList();
    this.getUsers();
  },
  computed: {
    isPlansExists() {
      return this.plans && this.plans.length > 0;
    },
    isAvailableAddNewUser() {
      return this.availableUsers && this.availableUsers.length > 0;
    },
    availableUsers() {
      const result = [];
      if (this.users && this.users.length > 0) {
        if (!this.roleUsers || this.roleUsers.length == 0) return this.users;
        else {
          this.users.forEach((x) => {
            if (!this.roleUsers.find((y) => y.Id == x.UserId)) result.push(x);
          });
        }
      }
      return result;
    },
    availableFullPermissions() {
      const result = [];
      if (this.rolePermissions && this.rolePermissions.length > 0) {
        this.rolePermissions.forEach((x) => {
          if (!this.limitedPermissions.find((y) => y == x.Id)) result.push(x);
        });
      }
      return result;
    },
    availableLimitedPermissions() {
      const result = [];

      if (this.rolePermissions && this.rolePermissions.length > 0) {
        this.rolePermissions.forEach((x) => {
          if (!this.fullPermissions.find((y) => y == x.Id)) result.push(x);
        });
      }
      return result;
    },
  },
  methods: {
    rowClick(item, row) {
      row.select(true);
      this.selectedRole = item;
      this.getRoleUsers(item.RoleId);
    },
    onEditRole(role) {
      if (role) {
        this.editRole = { ...role };
      } else {
        this.editRole = {
          RoleId: 0,
          Name: '',
          Description: '',
        };
      }
      this.editRoleDialog = true;
    },
    onCancelEditRole() {
      this.editRole = null;
      this.editRoleDialog = false;
    },
    onSaveEditRole() {
      this.updateRole(this.editRole);
    },
    onEditPermissions(role) {
      this.editRole = { ...role };
      this.getRolePermissions(this.editRole.RoleId);
      this.editRolePermissionsDialog = true;
    },
    onCancelPermissions() {
      this.editRole = null;
      this.rolePermissions = null;
      this.editRolePermissionsDialog = false;
    },
    onSavePermissions() {
      this.setRolePermissions();
      this.saveRolePermissions();
    },
    onCancelAddUsers() {
      this.newUser = null;
      this.addUserDialog = false;
    },
    onAddNewUser() {
      this.addUserToRole();
    },
    async onDeleteRole(role) {
      try {
        await this.$refs.confirmation.open(
          'Confirmation',
          'Are you sure you wish to delete this record?'
        );
        this.deleteRole(role.RoleId);
      } catch (err) {
        console.log(err);
      }
    },
    onManageUsers() {
      this.addUserDialog = true;
    },
    async onDeleteUser(user) {
      try {
        await this.$refs.confirmation.open(
          'Confirmation',
          'Are you sure you wish to delete this record?'
        );
        this.deleteUser(user.ShortName);
      } catch (err) {
        console.log(err);
      }
    },
    async updateRole(role) {
      try {
        await this.$api.post('/Roles/UpdateRole', role);

        this.$store.commit('setNotificationMessage', {
          type: 'success',
          text: 'Role was updated',
        });
        this.editRole = null;
        this.editRoleDialog = false;
        this.getRoles();
      } catch (err) {
        console.error(err);
      }
    },
    async getRoles() {
      try {
        this.loading = true;
        let response = await this.$api.get('/Roles/GetRoles');
        if (response.data) {
          this.roles = response.data;
        }
        this.loading = false;
      } catch (err) {
        this.loading = false;
        console.error(err);
      }
    },
    async deleteRole(roleId) {
      try {
        await this.$api.delete('/Roles/DeleteRole?roleId=' + roleId);

        this.getRoles();
      } catch (err) {
        console.error(err);
      }
    },
    async deleteUser(userName) {
      try {
        await this.$api.delete(
          '/Roles/DeleteUserRole?roleId=' + this.selectedRole.RoleId + '&userName=' + userName
        );

        this.getRoleUsers(this.selectedRole.RoleId);
      } catch (err) {
        console.error(err);
      }
    },
    async getRolePermissions(roleId) {
      try {
        this.loadingPermissions = true;
        let response = await this.$api.get('/Roles/GetRolePermissions?roleId=' + roleId);
        if (response.data) {
          this.rolePermissions = response.data;
          this.processRolePermissions(response.data);
        }
        this.loadingPermissions = false;
      } catch (err) {
        this.loadingPermissions = false;
        console.error(err);
      }
    },
    async getRoleUsers(roleId) {
      try {
        this.loadingUsers = true;
        this.roleUsers = null;
        let response = await this.$api.get('/Roles/GetRoleUsers?roleId=' + roleId);
        if (response.data) {
          this.roleUsers = response.data;
        }
        this.loadingUsers = false;
      } catch (err) {
        this.loadingUsers = false;
        console.error(err);
      }
    },
    async getPlanList() {
      try {
        let response = await this.$api.get('/plans/getfullplanlist');

        if (!response.data || !response.data.length) {
          const msg = 'Unable to load plans';
          this.$store.commit('setNotificationMessage', { type: 'error', text: msg });
          throw new Error(msg);
        }

        this.planList = response.data;
      } catch (err) {
        console.log(err);
      }
    },
    async getUsers() {
      try {
        let response = await this.$api.get('/User/GetuserList');
        if (response.data) {
          this.users = response.data;
        }
      } catch (err) {
        console.error(err);
      }
    },
    async addUserToRole() {
      try {
        const user = this.users.find((x) => {
          return x.UserId == this.newUser;
        });
        if (user) {
          await this.$api.post(
            '/Roles/AddUserToRole?roleId=' + this.selectedRole.RoleId + '&userName=' + user.UserName
          );

          this.$store.commit('setNotificationMessage', {
            type: 'success',
            text: 'Role added to user',
          });
          this.getRoleUsers(this.selectedRole.RoleId);
          this.addUserDialog = false;
        }
      } catch (err) {
        console.log(err.message);
      }
    },
    async saveRolePermissions() {
      try {
        const model = {
          RoleId: this.editRole.RoleId,
          Permissions: this.rolePermissions,
        };
        await this.$api.post('/Roles/SaveRolePermissions', model);

        this.$store.commit('setNotificationMessage', {
          type: 'success',
          text: 'Saved!',
        });
        this.onCancelPermissions();
      } catch (error) {
        console.log(error.message);
      } finally {
        this.loading = false;
      }
    },
    processRolePermissions(permissions) {
      const fillPermissions = [];
      const limitedPermissions = [];
      if (permissions && permissions.length > 0) {
        permissions.forEach((x) => {
          if (x.Reference == 'Full') fillPermissions.push(x.Id);
          if (x.Reference == 'Limited') limitedPermissions.push(x.Id);
        });
      }

      this.fullPermissions = fillPermissions;
      this.limitedPermissions = limitedPermissions;
    },

    setRolePermissions() {
      this.rolePermissions.forEach((x) => {
        let reference = 'Denied';
        let permission = this.fullPermissions.find((y) => x.Id == y);
        if (permission) reference = 'Full';
        else {
          let permission = this.limitedPermissions.find((y) => x.Id == y);
          if (permission) reference = 'Limited';
        }
        x.Reference = reference;
      });
    },
  },
};
</script>

<style scoped>
.roles {
  margin-top: 5px;
  margin-bottom: 10px;
  padding: 5px 30px;
}

.roles .areaTitle {
  display: flex;
  justify-content: space-between;
  margin-bottom: 5px;
}

.roles .actionBtnContainer {
  display: flex;
  justify-content: flex-end;
}

.roles .actionBtn {
  margin-top: 5px;
  margin-bottom: 5px;
}

.permissionsContainer {
  height: 500px;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 10px;
}

.permissionsContainer .permission {
  padding: 0px 5px 0px 5px;
}
.permissionsContainer .permission .v-text-field {
  padding-top: 0px;
}

.usersTitle {
  border-bottom: 1px solid lightgray;
  padding-bottom: 5px;
}

.manageIcon {
  font-size: 1rem;
}

.roles .theme--light.v-data-table tbody tr.v-data-table__selected {
  background: #4a1cd41f !important;
}

.roles .theme--light.v-data-table tbody tr {
  cursor: pointer;
}
</style>

<style lang="scss">
.permissionsContainer {
  .permission {
    .v-text-field__details {
      margin-bottom: 0px;
    }
  }
}
</style>
